import type { ChartConfiguration } from 'chart.js';
import type { HtmCodeGroupId } from '../code-group';
import { HTM_CODE_GROUP_ID_DISPLAY_NAMES } from '../code-group';
import type { HtmCaseCostData } from './case-cost-data';
import type { HtmDatalabelsChartDataset } from './cost-saving-chart-options';
import {
  COST_SAVINGS_CHART_DATASET_DATALABELS,
  COST_SAVINGS_CHART_OPTIONS,
} from './cost-saving-chart-options';

/** Costs per Hartmann code group and year, as the case-cost endpoint returns them. */
type HtmCaseCostByCodeGroup = {
  [groupId in HtmCodeGroupId]?: { [year: string]: number };
};

interface HtmCaseCostPart {
  data: HtmCaseCostByCodeGroup;
  total: number;
  chart: ChartConfiguration;
}

export class HtmOpportunityCostsClinicCaseCost {
  public grossCosts: HtmCaseCostPart;
  public netCosts: HtmCaseCostPart;
  public optimizationPotential: HtmCaseCostPart;

  constructor(
    caseCosts: HtmCaseCostData,
    private readonly _codeGroupIds: HtmCodeGroupId[],
    private readonly _year: number,
  ) {
    this._updateGrossCost(caseCosts.grossCosts);
    this._updateNetCost(caseCosts.netCosts);
    this._updateOptimizationPotential(caseCosts.optimizationPotential);
  }

  private _updateGrossCost(data: HtmCaseCostByCodeGroup): void {
    this.grossCosts = {
      data,
      total: this._getTotal(data),
      chart: this._getChart(data, 'Brutto Kosten'),
    };
  }

  private _updateNetCost(data: HtmCaseCostByCodeGroup): void {
    this.netCosts = {
      data,
      total: this._getTotal(data),
      chart: this._getChart(data, 'Netto Kosten'),
    };
  }

  private _updateOptimizationPotential(data: HtmCaseCostByCodeGroup): void {
    this.optimizationPotential = {
      data,
      total: this._getTotal(data),
      chart: this._getChart(data, 'Optimierungspotential'),
    };
  }

  private _getTotal(data: HtmCaseCostByCodeGroup): number {
    return Object.keys(data).reduce((previousValue, currentValue) => {
      if (
        data[currentValue as HtmCodeGroupId][this._year] &&
        !isNaN(data[currentValue as HtmCodeGroupId][this._year])
      ) {
        return previousValue + data[currentValue as HtmCodeGroupId][this._year];
      } else {
        return previousValue;
      }
    }, 0);
  }

  private _getChart(
    data: HtmCaseCostByCodeGroup,
    label: string,
  ): ChartConfiguration {
    const datasetData: number[] = this._codeGroupIds.map((groupId) => {
      return Math.round(data[groupId][this._year]);
    });
    const dataset: HtmDatalabelsChartDataset = {
      data: datasetData,
      label,
      backgroundColor: '#1a5e7e',
      datalabels: COST_SAVINGS_CHART_DATASET_DATALABELS,
    };

    return {
      type: 'bar',
      data: {
        datasets: [dataset],
        labels: this._codeGroupIds.map(
          (it) => HTM_CODE_GROUP_ID_DISPLAY_NAMES[it],
        ),
      },
      options: {
        plugins: {
          legend: { display: false },
        },
        ...COST_SAVINGS_CHART_OPTIONS,
        indexAxis: 'y',
      },
    };
  }
}
